import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  MapPin,
  Truck,
  Warehouse,
} from "lucide-react";
import type { GeoContent } from "@/data/geo-content";
import { RESPONSE_TIME_LINE } from "@/data/geo-content";

type Props = {
  content: GeoContent;
  locationName: string;
  stateName: string;
};

export function GeoSections({ content, locationName, stateName }: Props) {
  const c = content;
  return (
    <>
      {/* 1. Local Introduction */}
      <section className="py-14">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl uppercase">
            {locationName === stateName
              ? `Free Vending Machines in ${locationName}`
              : `Free Vending Machines in ${locationName}, ${stateName}`}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground/90">{c.intro}</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety/10 text-safety px-4 py-2 text-sm font-semibold">
            <Clock className="h-4 w-4" /> {RESPONSE_TIME_LINE}
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-6 text-sm">
            <InfoBlock title="Local Neighborhoods" items={c.neighborhoods} />
            <InfoBlock title="Service ZIP Zones" items={c.zips} />
            <InfoBlock title="Nearby Landmarks" items={c.landmarks} />
            <InfoBlock title="Local Industries" items={c.industries} />
          </div>
        </div>
      </section>

      {/* 2. Local Business Types */}
      <section className="py-14 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">
            Top Business Types We Serve in {locationName}
          </h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Qualifying employers across {locationName}, {stateName} that consistently win free
            vending placements:
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {c.businessTypes.map((t) => (
              <div
                key={t}
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-4"
              >
                <Building2 className="h-5 w-5 text-safety" />
                <span className="text-sm font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Local Service Coverage */}
      <section className="py-14">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">
            Local Service Coverage Across {locationName}
          </h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <CoverageCard
              icon={<MapPin className="h-5 w-5" />}
              title="Neighborhoods"
              items={c.coverageNeighborhoods}
            />
            <CoverageCard
              icon={<MapPin className="h-5 w-5" />}
              title="ZIP Zones"
              items={c.coverageZips}
            />
            <CoverageCard
              icon={<Warehouse className="h-5 w-5" />}
              title="Industrial Zones"
              items={c.industrialZones}
            />
            <CoverageCard
              icon={<Building2 className="h-5 w-5" />}
              title="Business Districts"
              items={c.businessDistricts}
            />
          </div>
        </div>
      </section>

      {/* 4. Local Operator Routing */}
      <section className="py-14 bg-secondary">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl uppercase">Local Operator Routing</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            <RoutingCard icon={<Truck />} title="Local Route Drivers" body={c.routing.drivers} />
            <RoutingCard icon={<Warehouse />} title="Nearby Depots" body={c.routing.depots} />
            <RoutingCard icon={<CheckCircle2 />} title="Refill Access" body={c.routing.refill} />
            <RoutingCard icon={<Clock />} title="Service Frequency" body={c.routing.frequency} />
          </div>
        </div>
      </section>

      {/* 5. Machine Recommendations */}
      <section className="py-14">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">
            Recommended Machines for {locationName}
          </h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Based on {locationName}'s climate and local business mix:
          </p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.machines.map((m) => (
              <div key={m.t} className="rounded-lg border border-border bg-card p-5">
                <div className="font-display text-lg uppercase text-steel">{m.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Local FAQ */}
      <section className="py-14 bg-secondary">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase">
            FAQ — Free Vending in {locationName}
          </h2>
          <div className="mt-8 divide-y divide-border border border-border rounded-xl bg-card">
            {c.faqs.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold list-none">
                  <span>{q}</span>
                  <span className="h-7 w-7 rounded-md bg-secondary flex items-center justify-center text-safety group-open:rotate-45 transition">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-sm text-center text-muted-foreground">{RESPONSE_TIME_LINE}</p>
        </div>
      </section>

      {/* 9. CTA */}
      <section id="apply" className="py-14">
        <div className="container-x text-center">
          <h2 className="font-display text-3xl md:text-4xl uppercase max-w-3xl mx-auto">
            {c.cta}
          </h2>
          <p className="mt-3 text-muted-foreground">{RESPONSE_TIME_LINE}</p>
          <Link
            to="/"
            hash="contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
          >
            Apply for Free Vending in {locationName} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function InfoBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="text-xs font-bold uppercase tracking-wider text-safety">{title}</div>
      <ul className="mt-3 space-y-1.5">
        {items.map((i) => (
          <li key={i} className="flex gap-2">
            <span className="text-safety">•</span>
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CoverageCard({
  icon,
  title,
  items,
}: {
  icon: React.ReactNode;
  title: string;
  items: string[];
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-steel font-semibold">
        {icon}
        <span>{title}</span>
      </div>
      <ul className="mt-3 text-sm space-y-1.5 text-foreground/85">
        {items.map((i) => (
          <li key={i}>• {i}</li>
        ))}
      </ul>
    </div>
  );
}

function RoutingCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-safety font-semibold">
        <span className="h-5 w-5">{icon}</span>
        <span>{title}</span>
      </div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}
